import axios, { AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { call, delay, put, StrictEffect, takeEvery, takeLatest } from 'redux-saga/effects';

import { getAggPageApiClient } from '../../common/services/agg-pages-api.service';
import { getProductsManagementApiClient } from '../../common/services/products-management-api.service';
import { actions } from '../actions';
import { aiImagesGenerationStatusSlice } from '../reducers/pages/ai-images-generation';
import { TKeywordCheckingState } from '../reducers/products/checkKeywordSlice';

type CheckKeywordActionType = ReturnType<typeof actions.checkKeyword>;
type CheckKeywordSagaType = Generator<
StrictEffect,
any,
AxiosResponse<{ exists: boolean; keyword: string }>
>;

function* checkKeywordSaga(action: CheckKeywordActionType): CheckKeywordSagaType {
  yield delay(500);
  yield put(actions.checkKeywordRequest(action.payload));
  try {
    let message: TKeywordCheckingState['message'];

    if (!action.payload.keyword.length) {
      message = {
        text: 'Field is required',
        type: 'error',
      };

      yield put(actions.checkKeywordsResponse(message));

      return;
    }

    const { data } = yield call(getAggPageApiClient().validateKeyword, action.payload);

    if (data.exists) {
      message = {
        text: 'Keyword already exists',
        type: 'error',
      };
    } else {
      message = {
        text: 'Keyword available',
        type: 'success',
      };
    }

    yield put(actions.checkKeywordsResponse(message));
  } catch (err) {
    let error;
    if (axios.isAxiosError(err)) {
      error = {
        code: err?.response?.status,
        message: err.message,
      };
    } else {
      error = { message: 'Something wrong' };
    }
    put(actions.checkKeywordError(error));
  }
}

function* productAiImagesGenerationStatusSaga(
  action: ReturnType<typeof aiImagesGenerationStatusSlice.actions.checkStatus>,
): Generator<StrictEffect, any, AxiosResponse<{ status: 'none' | 'in-progress' }>> {
  try {
    const url = window.location.href;
    let status = '';

    while (status !== 'none') {
      const { data } = yield call(getProductsManagementApiClient().getAiImagesGenerationStatus, action.payload.asins);

      if (data.status === 'none') {
        status = data.status;
        yield put(aiImagesGenerationStatusSlice.actions.changeEntity({
          id: action.payload.id,
          values: { isCompleted: window.location.href !== url, status: 'none' },
        }));
      } else if (data.status === 'in-progress') {
        status = data.status;
        yield delay(4000);
      } else {
        throw new Error('export failed');
      }
    }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      toast.error(err.message);
    } else {
      toast.error('Checking status failed');
    }
  }
}

export function* productsSagaWatcher() {
  yield takeLatest(actions.checkKeyword.type, checkKeywordSaga);
  yield takeEvery(aiImagesGenerationStatusSlice.actions.checkStatus.type, productAiImagesGenerationStatusSaga);
}
